import React, { useContext, useEffect, useMemo, useState } from "react";
import { Redirect } from "react-router-dom";
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import {
  Add,
  DeleteOutline,
  Edit,
  FileCopyOutlined,
  History,
  Refresh,
} from "@material-ui/icons";
import { toast } from "react-toastify";

import ConfirmationModal from "../../components/ConfirmationModal";
import ChatbotModal from "../../components/ChatbotModal";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import {
  customFieldToVariable,
  fetchContactCustomFieldNames,
  normalizeVariableName,
} from "../../utils/messageVariables";

const ALL_OUTBOUND_REFERENCES = "__all_outbound_references__";
const NO_AUTOMATION_SEQUENCE = "__no_automation_sequence__";
const NO_MAPPING_SOURCE = "__no_mapping_source__";
const NO_CUSTOM_FIELD = "__no_custom_field__";
const NO_WEBHOOK_CONNECTION = "__no_webhook_connection__";
const ALL_AI_WEBHOOK_CONNECTIONS = "__all_ai_webhook_connections__";
const NO_AUTOMATION_CONNECTION = "__no_automation_connection__";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    margin: 20,
    padding: theme.spacing(1.5),
    overflow: "auto",
    ...theme.scrollbarStyles,
  },
  toolbar: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    marginBottom: theme.spacing(1.5),
  },
  tableWrapper: {
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  table: {
    minWidth: 900,
  },
  mobileCards: {
    display: "none",
    [theme.breakpoints.down("xs")]: {
      display: "grid",
      gap: theme.spacing(1.5),
    },
  },
  mobileCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.paper,
  },
  mobileCardHeader: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: theme.spacing(1),
    alignItems: "flex-start",
  },
  mobileGrid: {
    display: "grid",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  actionsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 32px)",
    justifyContent: "end",
    alignItems: "center",
    gap: theme.spacing(0.25),
    "& .MuiIconButton-root": {
      width: 32,
      height: 32,
      padding: 6,
    },
  },
  label: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    textTransform: "uppercase",
  },
  value: {
    wordBreak: "break-word",
  },
  token: {
    fontFamily: '"Inter", sans-serif',
    wordBreak: "break-all",
  },
  codeBox: {
    background: theme.palette.type === "dark" ? "#1f2933" : "#f5f5f5",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(1.5),
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontFamily: '"Inter", sans-serif',
    fontSize: 12,
  },
  dialogGrid: {
    display: "grid",
    gap: theme.spacing(1.5),
  },
  stickyDialogTitle: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  dialogTitleContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
  },
  activeSwitch: {
    marginRight: 0,
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  endpointBox: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    alignItems: "center",
    gap: theme.spacing(1),
    background: theme.palette.type === "dark" ? "#1f2933" : "#f1f5f9",
    borderRadius: 6,
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
  },
  endpointText: {
    color: theme.palette.primary.main,
    fontFamily: '"Inter", sans-serif',
    fontSize: 12,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  statusBox: {
    borderRadius: 6,
    padding: theme.spacing(1.25),
    border: "1px solid transparent",
  },
  warningBox: {
    backgroundColor: "#fefce8",
    borderColor: "#fde68a",
    color: "#894b00",
  },
  successBox: {
    backgroundColor: "#f0fdf4",
    borderColor: "#bbf7d0",
    color: "#016630",
  },
  samplePreview: {
    display: "grid",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  samplePreviewHeader: {
    display: "grid",
    gridTemplateColumns: "minmax(120px, 0.8fr) minmax(160px, 1.4fr)",
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  samplePreviewRow: {
    display: "grid",
    gridTemplateColumns: "minmax(120px, 0.8fr) minmax(160px, 1.4fr)",
    gap: theme.spacing(1),
    alignItems: "start",
    padding: theme.spacing(0.75, 0),
    borderTop: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
      gap: theme.spacing(0.25),
    },
  },
  samplePreviewKey: {
    fontFamily: '"Inter", sans-serif',
    fontSize: 12,
    wordBreak: "break-word",
  },
  samplePreviewValue: {
    fontSize: 13,
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
  },
  samplePreviewActions: {
    marginTop: theme.spacing(1),
    display: "flex",
    justifyContent: "flex-start",
  },
  mappingRow: {
    display: "grid",
    gridTemplateColumns: "200px 1fr",
    gap: theme.spacing(1),
    alignItems: "start",
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  inboundMappingRow: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: theme.spacing(2),
    alignItems: "start",
    "& > *": {
      minWidth: 0,
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  mappingStaticLabel: {
    paddingTop: theme.spacing(2),
  },
  mappingActionColumn: {
    paddingTop: theme.spacing(1),
    display: "flex",
    justifyContent: "flex-start",
    "& .MuiButton-root": {
      whiteSpace: "nowrap",
    },
    [theme.breakpoints.down("xs")]: {
      paddingTop: 0,
    },
  },
  inlineSectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  mappingActions: {
    display: "flex",
    justifyContent: "flex-end",
  },
  builderRow: {
    display: "grid",
    gridTemplateColumns: "minmax(130px, 1fr) minmax(120px, 160px) minmax(180px, 1.5fr) auto",
    gap: theme.spacing(1),
    alignItems: "center",
    "& > *": {
      minWidth: 0,
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  payloadBuilderRow: {
    display: "grid",
    gridTemplateColumns: "minmax(150px, 1fr) minmax(180px, 1.4fr) auto",
    gap: theme.spacing(1),
    alignItems: "center",
    "& > *": {
      minWidth: 0,
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  compactSwitch: {
    marginLeft: 0,
    marginRight: theme.spacing(1),
    whiteSpace: "nowrap",
  },
  availabilityRow: {
    display: "grid",
    gridTemplateColumns: "120px minmax(110px, 1fr) 24px minmax(110px, 1fr)",
    gap: theme.spacing(1),
    alignItems: "center",
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  availabilitySeparator: {
    textAlign: "center",
    color: theme.palette.text.secondary,
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  logStatusSuccess: {
    backgroundColor: "#e7f5ea",
    color: "#1b5e20",
  },
  logStatusFailed: {
    backgroundColor: "#fdecea",
    color: "#b71c1c",
  },
}));

const initialForm = {
  name: "",
  connectionId: "",
  isActive: true,
};

const defaultAutomationMappings = [
  { target: "number", label: "Numero / ChatId", paths: [] },
  { target: "name", label: "Nombre", paths: [] },
  { target: "email", label: "Correo", paths: [] },
];

const initialAutomationForm = {
  name: "Nuevo disparador",
  connectionId: "",
  triggerType: "third_party",
  frequency: "always",
  isActive: true,
  token: "",
  fieldMappings: defaultAutomationMappings,
  runMode: "immediate",
  countdowns: [{ value: 0, unit: "minutes", sequenceId: "" }],
  chatbotSequenceId: "",
};

const outboundTriggerOptions = [
  { value: "sequence_executed", label: "Ejecucion de una secuencia" },
  { value: "label_added", label: "Agregar una etiqueta" },
  { value: "label_removed", label: "Quitar etiqueta" },
  { value: "funnel_stage_entered", label: "Entrar a etapa" },
  { value: "funnel_stage_left", label: "Salir de etapa" },
];

const outboundSourceOptions = [
  { value: "event", label: "Evento" },
  { value: "occurredAt", label: "Fecha del evento" },
  { value: "companyId", label: "ID de compania" },
  { value: "contact.id", label: "ID de contacto" },
  { value: "contact.name", label: "Nombre de contacto" },
  { value: "contact.number", label: "Numero de contacto" },
  { value: "contact.email", label: "Correo electronico" },
  { value: "ticket.id", label: "ID de ticket" },
  { value: "ticket.status", label: "Estado del ticket" },
  { value: "ticket.lastMessage", label: "Ultimo mensaje" },
  { value: "ticket.connectionId", label: "ID de conexion" },
  { value: "data.sequenceId", label: "ID de secuencia" },
  { value: "data.sequenceName", label: "Nombre de secuencia" },
  { value: "data.labelId", label: "ID de etiqueta" },
  { value: "data.label", label: "Nombre de etiqueta" },
  { value: "data.funnelId", label: "ID de embudo" },
  { value: "data.funnelName", label: "Nombre de embudo" },
  { value: "data.stageId", label: "ID de etapa" },
  { value: "data.stageName", label: "Nombre de etapa" },
];

const initialOutboundForm = {
  name: "",
  url: "",
  triggerType: "sequence_executed",
  triggerReferenceId: "",
  isActive: true,
  payloadMappings: [
    { key: "contacto.nombre", source: "contact.name" },
    { key: "contacto.telefono", source: "contact.number" },
    { key: "contacto.correo", source: "contact.email" },
  ],
};

const aiWebhookWeekdays = ["Dom", "Lun", "Mar", "Mie", "Jue", "Vie", "Sab"];

const defaultAiWebhookAvailabilitySettings = {
  durationMinutes: 30,
  intervalMinutes: 30,
  availability: [0, 1, 2, 3, 4, 5, 6].map(day => ({
    day,
    enabled: day > 0 && day < 6,
    startTime: "09:00",
    endTime: "17:00",
  })),
};

const initialAiWebhookToolForm = {
  name: "",
  toolName: "",
  description: "",
  method: "POST",
  url: "",
  connectionId: "",
  isActive: false,
  requiresConfirmation: false,
  timeoutMs: 15000,
  headers: [],
  parameters: [],
  payloadMappings: [],
  availabilitySettings: defaultAiWebhookAvailabilitySettings,
};

const normalizeToolName = value =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "");

const aiWebhookTypeOptions = [
  { value: "string", label: "Texto" },
  { value: "number", label: "Numero" },
  { value: "boolean", label: "Si / No" },
];

const aiWebhookContextOptions = [
  { value: "contact", label: "Contacto completo" },
  { value: "contact.name", label: "Nombre del contacto" },
  { value: "contact.number", label: "Telefono del contacto" },
  { value: "contact.email", label: "Correo del contacto" },
  { value: "ticket", label: "Ticket completo" },
  { value: "ticket.id", label: "ID del ticket" },
  { value: "ticket.status", label: "Estado del ticket" },
  { value: "ticket.connectionId", label: "ID de conexion" },
  { value: "companyId", label: "ID de compania" },
  { value: "timeZone", label: "Zona horaria" },
  { value: "calendarRange.timeMin", label: "Inicio del dia calendario" },
  { value: "calendarRange.timeMax", label: "Fin del dia calendario" },
  { value: "appointmentRange.inicio", label: "Inicio de cita calculado" },
  { value: "appointmentRange.fin", label: "Fin de cita calculado" },
];

const templateFromSource = source => `{{${source}}}`;

const sourceFromTemplate = value => {
  const match = String(value || "").match(/^{{\s*([^}]+)\s*}}$/);
  return match ? match[1].trim() : "";
};

const normalizeAiWebhookAvailabilitySettings = settings => {
  const source = settings || {};
  const availability = Array.isArray(source.availability)
    ? source.availability
    : defaultAiWebhookAvailabilitySettings.availability;

  return {
    durationMinutes: Number(source.durationMinutes || 30),
    intervalMinutes: Number(source.intervalMinutes || source.durationMinutes || 30),
    availability: [0, 1, 2, 3, 4, 5, 6].map(day => {
      const rule = availability.find(item => Number(item.day) === day) || {};
      const defaultRule = defaultAiWebhookAvailabilitySettings.availability[day];
      return {
        day,
        enabled: rule.enabled === undefined ? defaultRule.enabled : rule.enabled !== false,
        startTime: rule.startTime || defaultRule.startTime,
        endTime: rule.endTime || defaultRule.endTime,
      };
    }),
  };
};

const shouldShowAiWebhookAvailability = form =>
  normalizeToolName(form.toolName) === "consultar_horarios_disponibles";

const buildAiWebhookSchema = (
  parameters,
  availabilitySettings
) => {
  const cleanParameters = (parameters || []).filter(parameter => parameter.name?.trim());
  const schema = {
    type: "object",
    properties: cleanParameters.reduce((properties, parameter) => {
      const parameterName = parameter.name.trim();
      properties[parameterName] = {
        type: parameter.type || "string",
        description: parameter.description || "",
      };
      return properties;
    }, {}),
    required: cleanParameters
      .filter(parameter => parameter.required)
      .map(parameter => parameter.name.trim()),
    additionalProperties: false,
  };

  if (availabilitySettings) {
    schema.availabilitySettings = normalizeAiWebhookAvailabilitySettings(availabilitySettings);
  }

  return schema;
};

const buildAiWebhookBodyTemplate = mappings =>
  (mappings || [])
    .filter(mapping => mapping.key?.trim() && mapping.source)
    .reduce((payload, mapping) => {
      setPayloadPath(payload, mapping.key, templateFromSource(mapping.source));
      return payload;
    }, {});

const normalizeAiWebhookParameters = schema => {
  const properties = schema?.properties || {};
  const required = Array.isArray(schema?.required) ? schema.required : [];
  const parameters = Object.entries(properties).map(([name, config]) => ({
    name,
    type: config?.type || "string",
    description: config?.description || "",
    required: required.includes(name),
  }));

  return parameters;
};

const flattenAiWebhookTemplate = (value, prefix = "", rows = []) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return rows;

  Object.entries(value).forEach(([key, childValue]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (childValue && typeof childValue === "object" && !Array.isArray(childValue)) {
      flattenAiWebhookTemplate(childValue, path, rows);
      return;
    }
    rows.push({ key: path, source: sourceFromTemplate(childValue) });
  });

  return rows;
};

const normalizeAiWebhookPayloadMappings = template => {
  const mappings = flattenAiWebhookTemplate(template).filter(mapping => mapping.source);
  return mappings;
};

const formatDate = value => {
  if (!value) return "-";
  return new Date(value).toLocaleString();
};

const getWebhookEndpoint = () =>
  `${String(api.defaults.baseURL || window.location.origin).replace(/\/+$/, "")}/api/webhooks/send-message`;

const getAutomationEndpoint = token =>
  `${String(api.defaults.baseURL || window.location.origin).replace(/\/+$/, "")}/api/webhooks/automations/${token}`;

const generateAutomationToken = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, char => {
    const random = (Math.random() * 16) | 0;
    const value = char === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

const normalizeMappings = mappings => {
  const list = Array.isArray(mappings) ? mappings : [];
  const base = defaultAutomationMappings.map(defaultMapping => {
    const found = list.find(mapping => mapping.target === defaultMapping.target);
    return found ? { ...defaultMapping, ...found, paths: found.paths || [] } : defaultMapping;
  });
  const extras = list
    .filter(mapping => mapping.target === "extra")
    .map(mapping => ({ ...mapping, paths: mapping.paths || [], isNew: false }));
  return [...base, ...extras];
};

const normalizeCountdowns = countdowns => {
  const list = Array.isArray(countdowns) ? countdowns : [];
  const normalized = list.map(countdown => ({
    value: Math.max(0, Number(countdown.value || 0)),
    unit: countdown.unit || "minutes",
    sequenceId: countdown.sequenceId ? String(countdown.sequenceId) : "",
  }));

  return normalized.length
    ? normalized
    : [{ value: 0, unit: "minutes", sequenceId: "" }];
};

const formatSequenceOption = sequence => `ID: ${sequence.id} - ${sequence.name}`;

const formatOutboundTrigger = triggerType =>
  outboundTriggerOptions.find(option => option.value === triggerType)?.label || triggerType;

const setPayloadPath = (payload, path, value) => {
  const parts = String(path || "")
    .split(".")
    .map(part => part.trim())
    .filter(Boolean);
  if (!parts.length) return;

  let current = payload;
  parts.slice(0, -1).forEach(part => {
    if (!current[part] || typeof current[part] !== "object") current[part] = {};
    current = current[part];
  });

  current[parts[parts.length - 1]] = value;
};

const valueFromPath = (payload, path) => {
  if (!path) return undefined;

  return String(path)
    .split(".")
    .reduce((current, part) => {
      if (current === undefined || current === null) return undefined;
      return current[part];
    }, payload);
};

const formatSampleValue = value => {
  if (value === undefined) return "-";
  if (value === null) return "null";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return value || '""';

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const Webhooks = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [webhooks, setWebhooks] = useState([]);
  const [connections, setConnections] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [deletingWebhook, setDeletingWebhook] = useState(null);
  const [regeneratingWebhook, setRegeneratingWebhook] = useState(null);
  const [logsWebhook, setLogsWebhook] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [outboundWebhooks, setOutboundWebhooks] = useState([]);
  const [outboundModalOpen, setOutboundModalOpen] = useState(false);
  const [editingOutbound, setEditingOutbound] = useState(null);
  const [outboundForm, setOutboundForm] = useState(initialOutboundForm);
  const [deletingOutbound, setDeletingOutbound] = useState(null);
  const [logsOutbound, setLogsOutbound] = useState(null);
  const [outboundLogs, setOutboundLogs] = useState([]);
  const [loadingOutboundLogs, setLoadingOutboundLogs] = useState(false);
  const [aiWebhookTools, setAiWebhookTools] = useState([]);
  const [aiWebhookModalOpen, setAiWebhookModalOpen] = useState(false);
  const [editingAiWebhookTool, setEditingAiWebhookTool] = useState(null);
  const [aiWebhookForm, setAiWebhookForm] = useState(initialAiWebhookToolForm);
  const [deletingAiWebhookTool, setDeletingAiWebhookTool] = useState(null);
  const [resettingAiWebhookTool, setResettingAiWebhookTool] = useState(null);
  const [logsAiWebhookTool, setLogsAiWebhookTool] = useState(null);
  const [aiWebhookLogs, setAiWebhookLogs] = useState([]);
  const [loadingAiWebhookLogs, setLoadingAiWebhookLogs] = useState(false);
  const [automations, setAutomations] = useState([]);
  const [logsAutomation, setLogsAutomation] = useState(null);
  const [automationLogs, setAutomationLogs] = useState([]);
  const [loadingAutomationLogs, setLoadingAutomationLogs] = useState(false);
  const [automationModalOpen, setAutomationModalOpen] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState(null);
  const [automationForm, setAutomationForm] = useState(initialAutomationForm);
  const [deletingAutomation, setDeletingAutomation] = useState(null);
  const [sequences, setSequences] = useState([]);
  const [labels, setLabels] = useState([]);
  const [funnels, setFunnels] = useState([]);
  const [customFieldNames, setCustomFieldNames] = useState([]);
  const [sequenceModalOpen, setSequenceModalOpen] = useState(false);
  const [sequenceModalId, setSequenceModalId] = useState(null);
  const [sequenceTarget, setSequenceTarget] = useState({ type: "immediate", index: null });

  const canManage = ["admin", "superadmin", "owner"].includes(user?.profile);
  const endpoint = useMemo(() => getWebhookEndpoint(), []);
  const automationEndpoint = useMemo(
    () => getAutomationEndpoint(automationForm.token || editingAutomation?.token || ""),
    [automationForm.token, editingAutomation]
  );

  const fetchWebhooks = async () => {
    try {
      const { data } = await api.get("/webhooks");
      setWebhooks(data.webhooks || []);
    } catch (err) {
      toastError(err);
    }
  };

  const fetchAutomations = async () => {
    try {
      const { data } = await api.get("/webhooks/automations");
      const nextAutomations = data.automations || [];
      setAutomations(nextAutomations);
      return nextAutomations;
    } catch (err) {
      toastError(err);
      return [];
    }
  };

  const fetchOutboundWebhooks = async () => {
    try {
      const { data } = await api.get("/webhooks/outbounds");
      setOutboundWebhooks(data.outbounds || []);
      return data.outbounds || [];
    } catch (err) {
      toastError(err);
      return [];
    }
  };

  const fetchAiWebhookTools = async () => {
    try {
      const { data } = await api.get("/webhooks/ai-tools");
      setAiWebhookTools(data.tools || []);
      return data.tools || [];
    } catch (err) {
      toastError(err);
      return [];
    }
  };

  const fetchConnections = async () => {
    try {
      const { data } = await api.get("/connections/");
      setConnections((data || []).filter(connection => connection.channel !== "webchat"));
    } catch (err) {
      toastError(err);
    }
  };

  const fetchContacts = async () => {
    try {
      const { data } = await api.get("/contacts", {
        params: { pageNumber: 1, searchParam: "" },
      });
      setContacts(data.contacts || []);
      return data.contacts || [];
    } catch (err) {
      toastError(err);
      return [];
    }
  };

  const fetchSequences = async () => {
    try {
      const { data } = await api.get("/chatbot/sequences", {
        params: { all: true, pageNumber: 1, searchParam: "" },
      });
      setSequences(data.sequences || []);
      return data.sequences || [];
    } catch (err) {
      toastError(err);
      return [];
    }
  };

  const fetchLabels = async () => {
    try {
      const { data } = await api.get("/labels");
      setLabels(data || []);
      return data || [];
    } catch (err) {
      toastError(err);
      return [];
    }
  };

  const fetchFunnels = async () => {
    try {
      const { data } = await api.get("/funnels");
      setFunnels(data.funnels || []);
      return data.funnels || [];
    } catch (err) {
      toastError(err);
      return [];
    }
  };

  const fetchCustomFieldNames = async () => {
    try {
      const fields = await fetchContactCustomFieldNames();
      setCustomFieldNames(fields);
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    if (!canManage) return;
    fetchConnections();
    fetchContacts();
    fetchWebhooks();
    fetchAutomations();
    fetchOutboundWebhooks();
    fetchAiWebhookTools();
    fetchSequences();
    fetchLabels();
    fetchFunnels();
    fetchCustomFieldNames();
  }, [canManage]);

  useEffect(() => {
    if (!canManage) return undefined;

    const socket = openSocket();

    socket.on("webhookAutomation", data => {
      if (!data) return;

      if (data.action === "delete") {
        setAutomations(prevState =>
          prevState.filter(automation => automation.id !== data.automationId)
        );
        setEditingAutomation(prevState => {
          if (prevState?.id === data.automationId) {
            setAutomationModalOpen(false);
            return null;
          }
          return prevState;
        });
        return;
      }

      if (!data.automation) return;

      setAutomations(prevState => {
        const exists = prevState.some(automation => automation.id === data.automation.id);
        if (exists) {
          return prevState.map(automation =>
            automation.id === data.automation.id ? data.automation : automation
          );
        }
        return [data.automation, ...prevState];
      });

      setEditingAutomation(prevState => {
        if (!prevState) return prevState;
        return prevState.id === data.automation.id ? data.automation : prevState;
      });
    });

    return () => socket.disconnect();
  }, [canManage]);

  if (!user) return null;
  if (!canManage) return <Redirect to="/" />;

  const updateForm = patch => setForm(prevState => ({ ...prevState, ...patch }));
  const updateAutomationForm = patch =>
    setAutomationForm(prevState => ({ ...prevState, ...patch }));
  const updateOutboundForm = patch =>
    setOutboundForm(prevState => ({ ...prevState, ...patch }));
  const updateAiWebhookForm = patch =>
    setAiWebhookForm(prevState => ({ ...prevState, ...patch }));

  const retryAutomationListen = () => {
    if (!editingAutomation) return;

    const clearedAutomation = {
      ...editingAutomation,
      samplePayload: {},
      sampleFields: [],
    };

    setEditingAutomation(clearedAutomation);
    setAutomations(prevState =>
      prevState.map(automation =>
        automation.id === editingAutomation.id ? clearedAutomation : automation
      )
    );
    toast.info("Escucha reiniciada. Envia otra peticion de prueba al webhook.");
  };

  const mergeAutomation = automation => {
    setAutomations(prevState => {
      const exists = prevState.some(item => item.id === automation.id);
      if (exists) {
        return prevState.map(item => (item.id === automation.id ? automation : item));
      }
      return [automation, ...prevState];
    });
  };

  const copyText = async text => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copiado al portapapeles.");
    } catch {
      toast.error("No se pudo copiar.");
    }
  };

  const openCreateModal = () => {
    setEditingWebhook(null);
    setForm(initialForm);
    setModalOpen(true);
  };

  const openEditModal = webhook => {
    setEditingWebhook(webhook);
    setForm({
      name: webhook.name || "",
      connectionId: webhook.connectionId || "",
      isActive: webhook.isActive !== false,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingWebhook(null);
    setForm(initialForm);
  };

  const openCreateOutbound = () => {
    setEditingOutbound(null);
    setOutboundForm(initialOutboundForm);
    setOutboundModalOpen(true);
  };

  const openEditOutbound = outbound => {
    setEditingOutbound(outbound);
    setOutboundForm({
      name: outbound.name || "",
      url: outbound.url || "",
      triggerType: outbound.triggerType || "sequence_executed",
      triggerReferenceId: outbound.triggerReferenceId ? String(outbound.triggerReferenceId) : "",
      isActive: outbound.isActive !== false,
      payloadMappings: outbound.payloadMappings?.length
        ? outbound.payloadMappings
        : initialOutboundForm.payloadMappings,
    });
    setOutboundModalOpen(true);
  };

  const closeOutboundModal = () => {
    setOutboundModalOpen(false);
    setEditingOutbound(null);
    setOutboundForm(initialOutboundForm);
  };

  const openCreateAiWebhookTool = () => {
    setEditingAiWebhookTool(null);
    setAiWebhookForm(initialAiWebhookToolForm);
    setAiWebhookModalOpen(true);
  };

  const openEditAiWebhookTool = tool => {
    setEditingAiWebhookTool(tool);
    setAiWebhookForm({
      name: tool.name || "",
      toolName: tool.toolName || "",
      description: tool.description || "",
      method: tool.method || "POST",
      url: tool.url || "",
      connectionId: tool.connectionId || "",
      isActive: tool.isActive === true,
      requiresConfirmation: tool.requiresConfirmation === true,
      timeoutMs: tool.timeoutMs || 15000,
      headers: Object.entries(tool.headers || {}).map(([key, value]) => ({
        key,
        value: String(value || ""),
      })),
      parameters: normalizeAiWebhookParameters(tool.parametersSchema),
      payloadMappings: normalizeAiWebhookPayloadMappings(tool.bodyTemplate),
      availabilitySettings: normalizeAiWebhookAvailabilitySettings(
        tool.parametersSchema?.availabilitySettings
      ),
    });
    setAiWebhookModalOpen(true);
  };

  const closeAiWebhookModal = () => {
    setAiWebhookModalOpen(false);
    setEditingAiWebhookTool(null);
    setAiWebhookForm(initialAiWebhookToolForm);
  };

  const openCreateAutomation = async () => {
    const token = generateAutomationToken();
    const draft = {
      ...initialAutomationForm,
      token,
      fieldMappings: normalizeMappings(initialAutomationForm.fieldMappings),
    };

    try {
      const { data } = await api.post("/webhooks/automations", {
        ...draft,
        connectionId: null,
      });
      const automation = data.automation;
      setEditingAutomation(automation);
      setAutomationForm({
        ...draft,
        id: automation.id,
        name: automation.name || draft.name,
        token: automation.token,
        connectionId: automation.connectionId || "",
        triggerType: automation.triggerType || "third_party",
        frequency: automation.frequency || "always",
        isActive: automation.isActive !== false,
        fieldMappings: normalizeMappings(automation.fieldMappings),
        runMode: automation.runMode || "immediate",
        countdowns: normalizeCountdowns(automation.countdowns),
        chatbotSequenceId: automation.chatbotSequenceId
          ? String(automation.chatbotSequenceId)
          : "",
      });
      mergeAutomation(automation);
      setAutomationModalOpen(true);
    } catch (err) {
      toastError(err);
    }
  };

  const openEditAutomation = automation => {
    setEditingAutomation(automation);
    setAutomationForm({
      name: automation.name || "Nuevo disparador",
      connectionId: automation.connectionId || "",
      triggerType: automation.triggerType || "third_party",
      frequency: automation.frequency || "always",
      isActive: automation.isActive !== false,
      token: automation.token,
      fieldMappings: normalizeMappings(automation.fieldMappings),
      runMode: automation.runMode || "immediate",
      countdowns: normalizeCountdowns(automation.countdowns),
      chatbotSequenceId: automation.chatbotSequenceId
        ? String(automation.chatbotSequenceId)
        : "",
    });
    setAutomationModalOpen(true);
  };

  const closeAutomationModal = () => {
    setAutomationModalOpen(false);
    setEditingAutomation(null);
    setAutomationForm(initialAutomationForm);
  };

  const updateMapping = (index, patch) => {
    setAutomationForm(prevState => ({
      ...prevState,
      fieldMappings: prevState.fieldMappings.map((mapping, mappingIndex) =>
        mappingIndex === index ? { ...mapping, ...patch } : mapping
      ),
    }));
  };

  const addCustomMapping = () => {
    setAutomationForm(prevState => ({
      ...prevState,
      fieldMappings: [
        ...prevState.fieldMappings,
        { target: "extra", label: "", paths: [], isNew: false },
      ],
    }));
  };

  const removeCustomMapping = index => {
    setAutomationForm(prevState => ({
      ...prevState,
      fieldMappings: prevState.fieldMappings.filter(
        (_mapping, mappingIndex) => mappingIndex !== index
      ),
    }));
  };

  const openCreateSequence = (target = { type: "immediate", index: null }) => {
    setSequenceTarget(target);
    setSequenceModalId(null);
    setSequenceModalOpen(true);
  };

  const openEditSequence = (sequenceId, target = { type: "immediate", index: null }) => {
    if (!sequenceId) return;
    setSequenceTarget(target);
    setSequenceModalId(sequenceId);
    setSequenceModalOpen(true);
  };

  const updateCountdown = (index, patch) => {
    setAutomationForm(prevState => ({
      ...prevState,
      countdowns: normalizeCountdowns(prevState.countdowns).map((countdown, countdownIndex) =>
        countdownIndex === index ? { ...countdown, ...patch } : countdown
      ),
    }));
  };

  const addCountdown = () => {
    setAutomationForm(prevState => ({
      ...prevState,
      countdowns: [
        ...normalizeCountdowns(prevState.countdowns),
        { value: 0, unit: "minutes", sequenceId: "" },
      ],
    }));
  };

  const removeCountdown = index => {
    setAutomationForm(prevState => ({
      ...prevState,
      countdowns: normalizeCountdowns(prevState.countdowns).filter(
        (_countdown, countdownIndex) => countdownIndex !== index
      ),
    }));
  };

  const updateOutboundMapping = (index, patch) => {
    setOutboundForm(prevState => ({
      ...prevState,
      payloadMappings: prevState.payloadMappings.map((mapping, mappingIndex) =>
        mappingIndex === index ? { ...mapping, ...patch } : mapping
      ),
    }));
  };

  const addOutboundMapping = () => {
    setOutboundForm(prevState => ({
      ...prevState,
      payloadMappings: [...prevState.payloadMappings, { key: "", source: "" }],
    }));
  };

  const removeOutboundMapping = index => {
    setOutboundForm(prevState => ({
      ...prevState,
      payloadMappings: prevState.payloadMappings.filter(
        (_mapping, mappingIndex) => mappingIndex !== index
      ),
    }));
  };

  const handleSequenceSaved = async savedSequence => {
    const savedSequenceId = savedSequence?.id || sequenceModalId;
    const nextSequences = await fetchSequences();

    if (
      savedSequence &&
      savedSequenceId &&
      !nextSequences.some(sequence => String(sequence.id) === String(savedSequenceId))
    ) {
      setSequences(prevSequences => [...prevSequences, savedSequence]);
    }

    if (savedSequenceId) {
      if (sequenceTarget.type === "countdown") {
        updateCountdown(sequenceTarget.index, { sequenceId: String(savedSequenceId) });
      } else {
        updateAutomationForm({ chatbotSequenceId: String(savedSequenceId) });
      }
    }

    setSequenceModalOpen(false);
    setSequenceModalId(null);
    setSequenceTarget({ type: "immediate", index: null });
  };

  const handleSequenceModalClose = () => {
    setSequenceModalOpen(false);
    setSequenceModalId(null);
    setSequenceTarget({ type: "immediate", index: null });
  };

  const mappedCustomFieldNames = automationForm.fieldMappings
    .filter(mapping => mapping.target === "extra" && mapping.label)
    .map(mapping => mapping.label);

  const sequenceExtraVariables = Array.from(
    new Set([...customFieldNames, ...mappedCustomFieldNames].filter(Boolean))
  ).map(customFieldToVariable);

  const saveAutomation = async event => {
    event.preventDefault();

    if (!automationForm.name.trim()) {
      toast.error("Completa el nombre del disparador.");
      return;
    }

    const hasSampleFields = Boolean(editingAutomation?.sampleFields?.length);
    const phoneMapping = automationForm.fieldMappings.find(
      mapping => mapping.target === "number"
    );
    if (hasSampleFields && !phoneMapping?.paths?.length) {
      toast.error("Selecciona el campo del telefono para poder crear contactos.");
      return;
    }

    const hasCountdownSequence = normalizeCountdowns(automationForm.countdowns).some(
      countdown => countdown.sequenceId
    );
    if (
      (automationForm.chatbotSequenceId || hasCountdownSequence) &&
      !automationForm.connectionId
    ) {
      toast.error("Selecciona un dispositivo para ejecutar la secuencia.");
      return;
    }

    try {
      const payload = {
        ...automationForm,
        name: automationForm.name.trim(),
        connectionId: automationForm.connectionId || null,
        fieldMappings: automationForm.fieldMappings.map(({ isNew, ...mapping }) => mapping),
        chatbotSequenceId: automationForm.chatbotSequenceId || null,
        countdowns: normalizeCountdowns(automationForm.countdowns).filter(
          countdown => countdown.sequenceId
        ),
      };

      const { data } = editingAutomation?.id
        ? await api.put(`/webhooks/automations/${editingAutomation.id}`, payload)
        : await api.post("/webhooks/automations", payload);

      setEditingAutomation(data.automation);
      setAutomationForm(prevState => ({
        ...prevState,
        token: data.automation.token,
        fieldMappings: normalizeMappings(data.automation.fieldMappings),
        runMode: data.automation.runMode || "immediate",
        countdowns: normalizeCountdowns(data.automation.countdowns),
        chatbotSequenceId: data.automation.chatbotSequenceId
          ? String(data.automation.chatbotSequenceId)
          : "",
      }));
      mergeAutomation(data.automation);
      toast.success("Webhook de entrada guardado.");
      closeAutomationModal();
    } catch (err) {
      toastError(err);
    }
  };

  const deleteAutomation = async () => {
    if (!deletingAutomation) return;
    try {
      await api.delete(`/webhooks/automations/${deletingAutomation.id}`);
      setAutomations(prevState =>
        prevState.filter(automation => automation.id !== deletingAutomation.id)
      );
      setDeletingAutomation(null);
      toast.success("Webhook de entrada eliminado.");
    } catch (err) {
      toastError(err);
    }
  };

  const saveWebhook = async event => {
    event.preventDefault();

    if (!form.name.trim() || !form.connectionId) {
      toast.error("Completa el nombre y la conexion.");
      return;
    }

    try {
      if (editingWebhook) {
        await api.put(`/webhooks/${editingWebhook.id}`, form);
      } else {
        await api.post("/webhooks", form);
      }
      toast.success("Webhook de entrada texto directo guardado.");
      closeModal();
      fetchWebhooks();
    } catch (err) {
      toastError(err);
    }
  };

  const deleteWebhook = async () => {
    if (!deletingWebhook) return;
    try {
      await api.delete(`/webhooks/${deletingWebhook.id}`);
      setDeletingWebhook(null);
      toast.success("Webhook de entrada texto directo eliminado.");
      fetchWebhooks();
    } catch (err) {
      toastError(err);
    }
  };

  const saveOutbound = async event => {
    event.preventDefault();

    if (!outboundForm.name.trim() || !outboundForm.url.trim()) {
      toast.error("Completa el nombre y la URL destino.");
      return;
    }

    const payloadMappings = outboundForm.payloadMappings.filter(
      mapping => mapping.key?.trim() && mapping.source
    );

    if (!payloadMappings.length) {
      toast.error("Agrega al menos un campo para construir el payload.");
      return;
    }

    try {
      const payload = {
        ...outboundForm,
        name: outboundForm.name.trim(),
        url: outboundForm.url.trim(),
        triggerReferenceId: outboundForm.triggerReferenceId || null,
        payloadMappings,
      };

      if (editingOutbound) {
        await api.put(`/webhooks/outbounds/${editingOutbound.id}`, payload);
      } else {
        await api.post("/webhooks/outbounds", payload);
      }

      toast.success("Webhook de salida guardado.");
      closeOutboundModal();
      fetchOutboundWebhooks();
    } catch (err) {
      toastError(err);
    }
  };

  const deleteOutbound = async () => {
    if (!deletingOutbound) return;
    try {
      await api.delete(`/webhooks/outbounds/${deletingOutbound.id}`);
      setDeletingOutbound(null);
      toast.success("Webhook de salida eliminado.");
      fetchOutboundWebhooks();
    } catch (err) {
      toastError(err);
    }
  };

  const saveAiWebhookTool = async event => {
    event.preventDefault();

    if (!aiWebhookForm.name.trim() || !aiWebhookForm.toolName.trim()) {
      toast.error("Completa el nombre y el nombre de funcion.");
      return;
    }

    const parameters = aiWebhookForm.parameters
      .map(parameter => ({
        ...parameter,
        name: String(parameter.name || "").trim(),
      }))
      .filter(parameter => parameter.name);
    const payloadMappings = aiWebhookForm.payloadMappings.filter(
      mapping => mapping.key?.trim() && mapping.source
    );

    if (!parameters.length) {
      toast.error("Agrega al menos un dato para que la IA pueda usar la herramienta.");
      return;
    }

    if (!payloadMappings.length) {
      toast.error("Agrega al menos un campo para enviar a Make.");
      return;
    }

    try {
      const payload = {
        name: aiWebhookForm.name.trim(),
        toolName: normalizeToolName(aiWebhookForm.toolName),
        description: aiWebhookForm.description.trim(),
        method: aiWebhookForm.method,
        url: aiWebhookForm.url.trim(),
        connectionId: aiWebhookForm.connectionId || null,
        isActive: Boolean(aiWebhookForm.isActive),
        requiresConfirmation: Boolean(aiWebhookForm.requiresConfirmation),
        timeoutMs: Number(aiWebhookForm.timeoutMs || 15000),
        headers: aiWebhookForm.headers
          .filter(header => header.key?.trim())
          .reduce((headers, header) => {
            headers[header.key.trim()] = header.value || "";
            return headers;
          }, {}),
        parametersSchema: buildAiWebhookSchema(
          parameters,
          shouldShowAiWebhookAvailability(aiWebhookForm)
            ? aiWebhookForm.availabilitySettings
            : null
        ),
        bodyTemplate: buildAiWebhookBodyTemplate(payloadMappings),
      };

      if (!payload.description) {
        toast.error("Agrega una descripcion para que la IA sepa cuando usarla.");
        return;
      }

      if (editingAiWebhookTool) {
        await api.put(`/webhooks/ai-tools/${editingAiWebhookTool.id}`, payload);
      } else {
        await api.post("/webhooks/ai-tools", payload);
      }

      toast.success("Herramienta IA guardada.");
      closeAiWebhookModal();
      fetchAiWebhookTools();
    } catch (err) {
      toastError(err);
    }
  };

  const deleteAiWebhookTool = async () => {
    if (!deletingAiWebhookTool) return;
    try {
      await api.delete(`/webhooks/ai-tools/${deletingAiWebhookTool.id}`);
      setDeletingAiWebhookTool(null);
      toast.success("Herramienta IA eliminada.");
      fetchAiWebhookTools();
    } catch (err) {
      toastError(err);
    }
  };

  const resetAiWebhookTool = async () => {
    if (!resettingAiWebhookTool) return;
    try {
      await api.post(`/webhooks/ai-tools/${resettingAiWebhookTool.id}/reset`);
      setResettingAiWebhookTool(null);
      toast.success("Herramienta IA restablecida.");
      fetchAiWebhookTools();
    } catch (err) {
      toastError(err);
    }
  };

  const updateAiWebhookParameter = (index, patch) => {
    setAiWebhookForm(prevState => ({
      ...prevState,
      parameters: prevState.parameters.map((parameter, parameterIndex) =>
        parameterIndex === index ? { ...parameter, ...patch } : parameter
      ),
    }));
  };

  const addAiWebhookParameter = () => {
    setAiWebhookForm(prevState => ({
      ...prevState,
      parameters: [
        ...prevState.parameters,
        { name: "", type: "string", description: "", required: false },
      ],
    }));
  };

  const removeAiWebhookParameter = index => {
    setAiWebhookForm(prevState => ({
      ...prevState,
      parameters: prevState.parameters.filter(
        (_parameter, parameterIndex) => parameterIndex !== index
      ),
    }));
  };

  const updateAiWebhookPayloadMapping = (index, patch) => {
    setAiWebhookForm(prevState => ({
      ...prevState,
      payloadMappings: prevState.payloadMappings.map((mapping, mappingIndex) =>
        mappingIndex === index ? { ...mapping, ...patch } : mapping
      ),
    }));
  };

  const addAiWebhookPayloadMapping = () => {
    setAiWebhookForm(prevState => ({
      ...prevState,
      payloadMappings: [...prevState.payloadMappings, { key: "", source: "" }],
    }));
  };

  const removeAiWebhookPayloadMapping = index => {
    setAiWebhookForm(prevState => ({
      ...prevState,
      payloadMappings: prevState.payloadMappings.filter(
        (_mapping, mappingIndex) => mappingIndex !== index
      ),
    }));
  };

  const updateAiWebhookHeader = (index, patch) => {
    setAiWebhookForm(prevState => ({
      ...prevState,
      headers: prevState.headers.map((header, headerIndex) =>
        headerIndex === index ? { ...header, ...patch } : header
      ),
    }));
  };

  const updateAiWebhookAvailabilitySettings = patch => {
    setAiWebhookForm(prevState => ({
      ...prevState,
      availabilitySettings: normalizeAiWebhookAvailabilitySettings({
        ...prevState.availabilitySettings,
        ...patch,
      }),
    }));
  };

  const updateAiWebhookAvailabilityRule = (index, patch) => {
    setAiWebhookForm(prevState => {
      const settings = normalizeAiWebhookAvailabilitySettings(prevState.availabilitySettings);
      return {
        ...prevState,
        availabilitySettings: {
          ...settings,
          availability: settings.availability.map((rule, ruleIndex) =>
            ruleIndex === index ? { ...rule, ...patch } : rule
          ),
        },
      };
    });
  };

  const addAiWebhookHeader = () => {
    setAiWebhookForm(prevState => ({
      ...prevState,
      headers: [...prevState.headers, { key: "", value: "" }],
    }));
  };

  const removeAiWebhookHeader = index => {
    setAiWebhookForm(prevState => ({
      ...prevState,
      headers: prevState.headers.filter((_header, headerIndex) => headerIndex !== index),
    }));
  };

  const openAiWebhookLogs = async tool => {
    setLogsAiWebhookTool(tool);
    setAiWebhookLogs([]);
    setLoadingAiWebhookLogs(true);

    try {
      const { data } = await api.get(`/webhooks/ai-tools/${tool.id}/logs`);
      setAiWebhookLogs(data.logs || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingAiWebhookLogs(false);
    }
  };

  const testOutbound = async () => {
    if (!outboundForm.url.trim()) {
      toast.error("Agrega la URL destino para ejecutar la prueba.");
      return;
    }

    const payloadMappings = outboundForm.payloadMappings.filter(
      mapping => mapping.key?.trim() && mapping.source
    );

    if (!payloadMappings.length) {
      toast.error("Agrega al menos un campo para construir el payload de prueba.");
      return;
    }

    try {
      const { data } = await api.post("/webhooks/outbounds/test", {
        url: outboundForm.url.trim(),
        triggerType: outboundForm.triggerType,
        triggerReferenceId: outboundForm.triggerReferenceId || null,
        payloadMappings,
      });

      if (data.success) {
        toast.success(`Prueba enviada correctamente (${data.statusCode}).`);
      } else {
        toast.error(`La URL respondio con estado ${data.statusCode}.`);
      }
    } catch (err) {
      toastError(err);
    }
  };

  const testAiWebhookTool = async () => {
    if (!editingAiWebhookTool?.id) {
      toast.error("Guarda la herramienta antes de ejecutar el webhook.");
      return;
    }

    try {
      const { data } = await api.post(
        `/webhooks/ai-tools/${editingAiWebhookTool.id}/test`,
        { args: {} }
      );

      if (data.success === false) {
        toast.error("El webhook respondio con error.");
        return;
      }

      toast.success("Webhook ejecutado correctamente.");
    } catch (err) {
      toastError(err);
    }
  };

  const regenerateToken = async () => {
    if (!regeneratingWebhook) return;
    try {
      await api.post(`/webhooks/${regeneratingWebhook.id}/regenerate-token`);
      setRegeneratingWebhook(null);
      toast.success("Token regenerado.");
      fetchWebhooks();
    } catch (err) {
      toastError(err);
    }
  };

  const openLogs = async webhook => {
    setLogsWebhook(webhook);
    setLogs([]);
    setLoadingLogs(true);

    try {
      const { data } = await api.get(`/webhooks/${webhook.id}/logs`);
      setLogs(data.logs || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingLogs(false);
    }
  };

  const buildAppScript = webhook => `function sendToWA(phone, msg) {
  var payload = {
    target: phone,
    message: msg,
    token: "${webhook.token}"
  };

  var response = UrlFetchApp.fetch(
    "${endpoint}",
    {
      method: "POST",
      contentType: "application/json",
      payload: JSON.stringify(payload),
      muteHttpExceptions: true
    }
  );

  return JSON.parse(response.getContentText());
}`;

  const renderActions = webhook => (
    <div className={classes.actionsGrid}>
      <Tooltip title="Copiar endpoint">
        <IconButton size="small" onClick={() => copyText(endpoint)}>
          <FileCopyOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Logs">
        <IconButton size="small" onClick={() => openLogs(webhook)}>
          <History fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Editar">
        <IconButton size="small" onClick={() => openEditModal(webhook)}>
          <Edit fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Regenerar token">
        <IconButton size="small" onClick={() => setRegeneratingWebhook(webhook)}>
          <Refresh fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Eliminar">
        <IconButton size="small" onClick={() => setDeletingWebhook(webhook)}>
          <DeleteOutline fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
  );

  const renderAutomationActions = automation => (
    <div className={classes.actionsGrid}>
      <Tooltip title="Copiar URL">
        <IconButton size="small" onClick={() => copyText(automation.endpoint)}>
          <FileCopyOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Logs">
        <IconButton size="small" onClick={() => openAutomationLogs(automation)}>
          <History fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Configurar">
        <IconButton size="small" onClick={() => openEditAutomation(automation)}>
          <Edit fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Eliminar">
        <IconButton size="small" onClick={() => setDeletingAutomation(automation)}>
          <DeleteOutline fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
  );

  const outboundReferenceOptions = () => {
    if (outboundForm.triggerType === "sequence_executed") {
      return sequences.map(sequence => ({
        value: String(sequence.id),
        label: formatSequenceOption(sequence),
      }));
    }

    if (["label_added", "label_removed"].includes(outboundForm.triggerType)) {
      return labels.map(label => ({
        value: String(label.id),
        label: label.name,
      }));
    }

    if (["funnel_stage_entered", "funnel_stage_left"].includes(outboundForm.triggerType)) {
      return funnels.flatMap(funnel =>
        (funnel.stages || []).map(stage => ({
          value: String(stage.id),
          label: `${funnel.name} - ${stage.name}`,
        }))
      );
    }

    return [];
  };

  const outboundReferenceLabel = triggerType => {
    if (triggerType === "sequence_executed") return "Secuencia";
    if (["label_added", "label_removed"].includes(triggerType)) return "Etiqueta";
    if (["funnel_stage_entered", "funnel_stage_left"].includes(triggerType)) return "Etapa";
    return "Referencia";
  };

  const renderOutboundReferenceSelector = () => (
    <FormControl variant="outlined" margin="dense" fullWidth>
      <InputLabel>{outboundReferenceLabel(outboundForm.triggerType)}</InputLabel>
      <Select
        value={outboundForm.triggerReferenceId || ALL_OUTBOUND_REFERENCES}
        onChange={event =>
          updateOutboundForm({
            triggerReferenceId:
              event.target.value === ALL_OUTBOUND_REFERENCES ? "" : event.target.value,
          })
        }
        label={outboundReferenceLabel(outboundForm.triggerType)}
      >
        <MenuItem value={ALL_OUTBOUND_REFERENCES}>Todos</MenuItem>
        {outboundReferenceOptions().map(option => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const formatOutboundReference = outbound => {
    if (!outbound.triggerReferenceId) return "Todos";
    const options = (() => {
      if (outbound.triggerType === "sequence_executed") {
        return sequences.map(sequence => ({
          value: String(sequence.id),
          label: formatSequenceOption(sequence),
        }));
      }
      if (["label_added", "label_removed"].includes(outbound.triggerType)) {
        return labels.map(label => ({ value: String(label.id), label: label.name }));
      }
      return funnels.flatMap(funnel =>
        (funnel.stages || []).map(stage => ({
          value: String(stage.id),
          label: `${funnel.name} - ${stage.name}`,
        }))
      );
    })();

    return (
      options.find(option => option.value === String(outbound.triggerReferenceId))?.label ||
      `ID: ${outbound.triggerReferenceId}`
    );
  };

const outboundSourceOptionsWithCustomFields = [
  ...outboundSourceOptions,
  ...customFieldNames.filter(Boolean).map(fieldName => ({
    value: `contact.customFields.${normalizeVariableName(fieldName)}`,
    label: `Campo personalizado: ${fieldName}`,
  })),
];

const aiWebhookArgumentSourceOptions = aiWebhookForm.parameters
  .filter(parameter => parameter.name?.trim())
  .flatMap(parameter => {
    const parameterName = parameter.name.trim();
    const normalizedName = normalizeToolName(parameterName);
    const options = [
      {
        value: `args.${parameterName}`,
        label: `Dato de IA: ${parameterName}`,
      },
    ];

    if (normalizedName && normalizedName !== parameterName) {
      options.push({
        value: `args.${normalizedName}`,
        label: `Dato de IA: ${normalizedName}`,
      });
    }

    return options;
  });

const aiWebhookPayloadSourceOptions = [
  ...aiWebhookArgumentSourceOptions,
  ...aiWebhookContextOptions,
];

  const renderAiWebhookParameterRow = (parameter, index) => (
    <div className={classes.builderRow} key={`ai-webhook-parameter-${index}`}>
      <TextField
        label="Dato"
        value={parameter.name || ""}
        onChange={event =>
          updateAiWebhookParameter(index, { name: event.target.value })
        }
        variant="outlined"
        margin="dense"
        placeholder="fecha"
      />
      <FormControl variant="outlined" margin="dense" fullWidth>
        <InputLabel>Tipo</InputLabel>
        <Select
          value={parameter.type || "string"}
          onChange={event => updateAiWebhookParameter(index, { type: event.target.value })}
          label="Tipo"
        >
          {aiWebhookTypeOptions.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        label="Descripcion"
        value={parameter.description || ""}
        onChange={event =>
          updateAiWebhookParameter(index, { description: event.target.value })
        }
        variant="outlined"
        margin="dense"
        placeholder="Fecha local solicitada por el cliente"
      />
      <div>
        <FormControlLabel
          className={classes.compactSwitch}
          control={
            <Switch
              color="primary"
              checked={Boolean(parameter.required)}
              onChange={event =>
                updateAiWebhookParameter(index, { required: event.target.checked })
              }
            />
          }
          label="Requerido"
        />
        <IconButton
          size="small"
          onClick={() => removeAiWebhookParameter(index)}
        >
          <DeleteOutline fontSize="small" />
        </IconButton>
      </div>
    </div>
  );

  const renderAiWebhookPayloadRow = (mapping, index) => (
    <div className={classes.payloadBuilderRow} key={`ai-webhook-payload-${index}`}>
      <TextField
        label="Nombre en Make"
        value={mapping.key || ""}
        onChange={event =>
          updateAiWebhookPayloadMapping(index, { key: event.target.value })
        }
        variant="outlined"
        margin="dense"
        placeholder="fecha"
      />
      <FormControl variant="outlined" margin="dense" fullWidth>
        <InputLabel>Dato a enviar</InputLabel>
        <Select
          value={mapping.source || NO_MAPPING_SOURCE}
          onChange={event =>
            updateAiWebhookPayloadMapping(index, {
              source: event.target.value === NO_MAPPING_SOURCE ? "" : event.target.value,
            })
          }
          label="Dato a enviar"
        >
          <MenuItem value={NO_MAPPING_SOURCE}>Sin dato</MenuItem>
          {aiWebhookPayloadSourceOptions.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <IconButton
        size="small"
        onClick={() => removeAiWebhookPayloadMapping(index)}
      >
        <DeleteOutline fontSize="small" />
      </IconButton>
    </div>
  );

  const renderAiWebhookAvailabilitySettings = () => {
    const settings = normalizeAiWebhookAvailabilitySettings(
      aiWebhookForm.availabilitySettings
    );

    return (
      <div className={classes.dialogGrid}>
        <div className={classes.inlineSectionHeader}>
          <div>
            <Typography variant="subtitle2">Calculo de disponibilidad</Typography>
            <Typography variant="body2" color="textSecondary">
              Define la duracion, el bloque de minutos y los horarios laborales por dia.
            </Typography>
          </div>
        </div>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Duracion"
              type="number"
              value={settings.durationMinutes}
              onChange={event =>
                updateAiWebhookAvailabilitySettings({
                  durationMinutes: event.target.value,
                })
              }
              helperText="Minutos"
              variant="outlined"
              margin="dense"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Bloque"
              type="number"
              value={settings.intervalMinutes}
              onChange={event =>
                updateAiWebhookAvailabilitySettings({
                  intervalMinutes: event.target.value,
                })
              }
              helperText="Minutos"
              variant="outlined"
              margin="dense"
              fullWidth
            />
          </Grid>
        </Grid>

        {settings.availability.map((rule, index) => (
          <div className={classes.availabilityRow} key={`ai-availability-${rule.day}`}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={rule.enabled !== false}
                  onChange={event =>
                    updateAiWebhookAvailabilityRule(index, {
                      enabled: event.target.checked,
                    })
                  }
                />
              }
              label={aiWebhookWeekdays[rule.day]}
            />
            <TextField
              type="time"
              value={rule.startTime || "09:00"}
              variant="outlined"
              margin="dense"
              disabled={rule.enabled === false}
              onChange={event =>
                updateAiWebhookAvailabilityRule(index, {
                  startTime: event.target.value,
                })
              }
            />
            <Typography className={classes.availabilitySeparator}>a</Typography>
            <TextField
              type="time"
              value={rule.endTime || "17:00"}
              variant="outlined"
              margin="dense"
              disabled={rule.enabled === false}
              onChange={event =>
                updateAiWebhookAvailabilityRule(index, {
                  endTime: event.target.value,
                })
              }
            />
          </div>
        ))}
      </div>
    );
  };

  const renderAiWebhookHeaderRow = (header, index) => (
    <div className={classes.payloadBuilderRow} key={`ai-webhook-header-${index}`}>
      <TextField
        label="Header"
        value={header.key || ""}
        onChange={event => updateAiWebhookHeader(index, { key: event.target.value })}
        variant="outlined"
        margin="dense"
        placeholder="Authorization"
      />
      <TextField
        label="Valor"
        value={header.value || ""}
        onChange={event => updateAiWebhookHeader(index, { value: event.target.value })}
        variant="outlined"
        margin="dense"
        placeholder="Bearer ..."
      />
      <IconButton size="small" onClick={() => removeAiWebhookHeader(index)}>
        <DeleteOutline fontSize="small" />
      </IconButton>
    </div>
  );

  const renderOutboundActions = outbound => (
    <div className={classes.actionsGrid}>
      <Tooltip title="Copiar URL">
        <IconButton size="small" onClick={() => copyText(outbound.url)}>
          <FileCopyOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Logs">
        <IconButton size="small" onClick={() => openOutboundLogs(outbound)}>
          <History fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Editar">
        <IconButton size="small" onClick={() => openEditOutbound(outbound)}>
          <Edit fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Eliminar">
        <IconButton size="small" onClick={() => setDeletingOutbound(outbound)}>
          <DeleteOutline fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
  );

  const renderAiWebhookActions = tool => (
    <div className={classes.actionsGrid}>
      <Tooltip title="Logs">
        <IconButton size="small" onClick={() => openAiWebhookLogs(tool)}>
          <History fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Editar">
        <IconButton size="small" onClick={() => openEditAiWebhookTool(tool)}>
          <Edit fontSize="small" />
        </IconButton>
      </Tooltip>
      {tool.isDefault && (
        <Tooltip title="Restablecer">
          <IconButton size="small" onClick={() => setResettingAiWebhookTool(tool)}>
            <Refresh fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {!tool.isDefault && (
        <Tooltip title="Eliminar">
          <IconButton size="small" onClick={() => setDeletingAiWebhookTool(tool)}>
            <DeleteOutline fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </div>
  );

  const renderWebhookRow = webhook => (
    <TableRow key={webhook.id}>
      <TableCell>{webhook.name}</TableCell>
      <TableCell>{webhook.connection?.name || "-"}</TableCell>
      <TableCell>
        <Typography className={classes.token} variant="body2">
          {webhook.token}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Chip
          size="small"
          label={webhook.isActive ? "Activo" : "Inactivo"}
          color={webhook.isActive ? "primary" : "default"}
        />
      </TableCell>
      <TableCell align="center">{webhook.sentCount || 0}</TableCell>
      <TableCell align="center">{webhook.failedCount || 0}</TableCell>
      <TableCell>{formatDate(webhook.lastUsedAt)}</TableCell>
      <TableCell align="right">{renderActions(webhook)}</TableCell>
    </TableRow>
  );

  const renderMobileWebhook = webhook => (
    <div className={classes.mobileCard} key={webhook.id}>
      <div className={classes.mobileCardHeader}>
        <div>
          <Typography variant="subtitle2">{webhook.name}</Typography>
          <Typography variant="body2" color="textSecondary">
            {webhook.connection?.name || "-"}
          </Typography>
        </div>
        <Chip
          size="small"
          label={webhook.isActive ? "Activo" : "Inactivo"}
          color={webhook.isActive ? "primary" : "default"}
        />
      </div>
      <div className={classes.mobileGrid}>
        <div>
          <span className={classes.label}>Token</span>
          <Typography className={`${classes.value} ${classes.token}`} variant="body2">
            {webhook.token}
          </Typography>
        </div>
        <div>
          <span className={classes.label}>Envios</span>
          <Typography variant="body2">
            {webhook.sentCount || 0} enviados - {webhook.failedCount || 0} fallidos
          </Typography>
        </div>
        <div>{renderActions(webhook)}</div>
      </div>
    </div>
  );

  const renderAutomationRow = automation => (
    <TableRow key={automation.id}>
      <TableCell>{automation.name}</TableCell>
      <TableCell>{automation.connection?.name || "Sin dispositivo"}</TableCell>
      <TableCell>
        <Typography className={classes.token} variant="body2">
          {automation.endpoint}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Chip
          size="small"
          label={automation.isActive ? "Activo" : "Inactivo"}
          color={automation.isActive ? "primary" : "default"}
        />
      </TableCell>
      <TableCell align="center">{automation.processedCount || 0}</TableCell>
      <TableCell align="center">{automation.failedCount || 0}</TableCell>
      <TableCell>{formatDate(automation.lastReceivedAt)}</TableCell>
      <TableCell align="right">{renderAutomationActions(automation)}</TableCell>
    </TableRow>
  );

  const renderMobileAutomation = automation => (
    <div className={classes.mobileCard} key={automation.id}>
      <div className={classes.mobileCardHeader}>
        <div>
          <Typography variant="subtitle2">{automation.name}</Typography>
          <Typography variant="body2" color="textSecondary">
            {automation.connection?.name || "Sin dispositivo"}
          </Typography>
        </div>
        <Chip
          size="small"
          label={automation.isActive ? "Activo" : "Inactivo"}
          color={automation.isActive ? "primary" : "default"}
        />
      </div>
      <div className={classes.mobileGrid}>
        <div>
          <span className={classes.label}>URL</span>
          <Typography className={`${classes.value} ${classes.token}`} variant="body2">
            {automation.endpoint}
          </Typography>
        </div>
        <div>
          <span className={classes.label}>Ejecuciones</span>
          <Typography variant="body2">
            {automation.processedCount || 0} procesadas - {automation.failedCount || 0} fallidas
          </Typography>
        </div>
        <div>{renderAutomationActions(automation)}</div>
      </div>
    </div>
  );

  const renderOutboundRow = outbound => (
    <TableRow key={outbound.id}>
      <TableCell>{outbound.name}</TableCell>
      <TableCell>{formatOutboundTrigger(outbound.triggerType)}</TableCell>
      <TableCell>{formatOutboundReference(outbound)}</TableCell>
      <TableCell>
        <Typography className={classes.token} variant="body2">
          {outbound.url}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Chip
          size="small"
          label={outbound.isActive ? "Activo" : "Inactivo"}
          color={outbound.isActive ? "primary" : "default"}
        />
      </TableCell>
      <TableCell align="center">{outbound.sentCount || 0}</TableCell>
      <TableCell align="center">{outbound.failedCount || 0}</TableCell>
      <TableCell>{formatDate(outbound.lastTriggeredAt)}</TableCell>
      <TableCell align="right">{renderOutboundActions(outbound)}</TableCell>
    </TableRow>
  );

  const renderAiWebhookRow = tool => (
    <TableRow key={tool.id}>
      <TableCell>{tool.name}</TableCell>
      <TableCell>
        <Typography className={classes.token} variant="body2">
          {tool.toolName}
        </Typography>
      </TableCell>
      <TableCell>{tool.connection?.name || "Todas"}</TableCell>
      <TableCell>
        <Typography className={classes.token} variant="body2">
          {tool.url || "Sin URL configurada"}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Chip
          size="small"
          label={tool.isActive ? "Activo" : "Inactivo"}
          color={tool.isActive ? "primary" : "default"}
        />
      </TableCell>
      <TableCell align="center">{tool.sentCount || 0}</TableCell>
      <TableCell align="center">{tool.failedCount || 0}</TableCell>
      <TableCell>{formatDate(tool.lastTriggeredAt)}</TableCell>
      <TableCell align="right">{renderAiWebhookActions(tool)}</TableCell>
    </TableRow>
  );

  const renderMobileAiWebhook = tool => (
    <div className={classes.mobileCard} key={tool.id}>
      <div className={classes.mobileCardHeader}>
        <div>
          <Typography variant="subtitle2">{tool.name}</Typography>
          <Typography className={classes.token} variant="body2" color="textSecondary">
            {tool.toolName}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {tool.connection?.name || "Todas las conexiones"}
          </Typography>
        </div>
        <Chip
          size="small"
          label={tool.isActive ? "Activo" : "Inactivo"}
          color={tool.isActive ? "primary" : "default"}
        />
      </div>
      <div className={classes.mobileGrid}>
        <div>
          <span className={classes.label}>URL Make</span>
          <Typography className={`${classes.value} ${classes.token}`} variant="body2">
            {tool.url || "Sin URL configurada"}
          </Typography>
        </div>
        <div>
          <span className={classes.label}>Ejecuciones</span>
          <Typography variant="body2">
            {tool.sentCount || 0} correctas - {tool.failedCount || 0} fallidas
          </Typography>
        </div>
        <div>{renderAiWebhookActions(tool)}</div>
      </div>
    </div>
  );

  const renderMobileOutbound = outbound => (
    <div className={classes.mobileCard} key={outbound.id}>
      <div className={classes.mobileCardHeader}>
        <div>
          <Typography variant="subtitle2">{outbound.name}</Typography>
          <Typography variant="body2" color="textSecondary">
            {formatOutboundTrigger(outbound.triggerType)}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {formatOutboundReference(outbound)}
          </Typography>
        </div>
        <Chip
          size="small"
          label={outbound.isActive ? "Activo" : "Inactivo"}
          color={outbound.isActive ? "primary" : "default"}
        />
      </div>
      <div className={classes.mobileGrid}>
        <div>
          <span className={classes.label}>URL destino</span>
          <Typography className={`${classes.value} ${classes.token}`} variant="body2">
            {outbound.url}
          </Typography>
        </div>
        <div>
          <span className={classes.label}>Envios</span>
          <Typography variant="body2">
            {outbound.sentCount || 0} enviados - {outbound.failedCount || 0} fallidos
          </Typography>
        </div>
        <div>{renderOutboundActions(outbound)}</div>
      </div>
    </div>
  );

  const renderAutomationSequenceSelector = ({
    value = automationForm.chatbotSequenceId,
    onChange = sequenceId => updateAutomationForm({ chatbotSequenceId: sequenceId }),
    target = { type: "immediate", index: null },
  } = {}) => (
    <>
      <FormControl variant="outlined" margin="dense" fullWidth>
        <InputLabel>Secuencia</InputLabel>
        <Select
          value={value || NO_AUTOMATION_SEQUENCE}
          label="Secuencia"
          onChange={event => {
            if (event.target.value === "__create__") {
              openCreateSequence(target);
              return;
            }
            onChange(event.target.value === NO_AUTOMATION_SEQUENCE ? "" : event.target.value);
          }}
        >
          <MenuItem value={NO_AUTOMATION_SEQUENCE}>No ejecutar secuencia</MenuItem>
          <MenuItem value="__create__">Crear secuencia...</MenuItem>
          {sequences.map(sequence => (
            <MenuItem key={sequence.id} value={String(sequence.id)}>
              {formatSequenceOption(sequence)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button
        color="primary"
        disabled={!value}
        onClick={() => openEditSequence(value, target)}
      >
        Editar secuencia
      </Button>
    </>
  );

  const renderMappingRow = (mapping, index) => {
    const sampleFields = editingAutomation?.sampleFields || [];
    const disabled = !sampleFields.length;
    const customFieldValue = mapping.isNew ? "__new__" : mapping.label || NO_CUSTOM_FIELD;
    const showCurrentCustomField =
      mapping.target === "extra" &&
      mapping.label &&
      !customFieldNames.includes(mapping.label);
    const label =
      mapping.target === "extra" ? (
        <>
          <FormControl variant="outlined" margin="dense" fullWidth>
            <InputLabel>Campo personalizado</InputLabel>
            <Select
              value={customFieldValue}
              onChange={event => {
                if (event.target.value === "__new__") {
                  updateMapping(index, { label: "", isNew: true });
                  return;
                }

                updateMapping(index, {
                  label: event.target.value === NO_CUSTOM_FIELD ? "" : event.target.value,
                  isNew: false,
                });
              }}
              label="Campo personalizado"
            >
              <MenuItem value={NO_CUSTOM_FIELD}>Seleccionar campo</MenuItem>
              {customFieldNames.map(fieldName => (
                <MenuItem key={fieldName} value={fieldName}>
                  {fieldName}
                </MenuItem>
              ))}
              {showCurrentCustomField && (
                <MenuItem value={mapping.label}>{mapping.label}</MenuItem>
              )}
              <MenuItem value="__new__">Crear nuevo...</MenuItem>
            </Select>
          </FormControl>
          {mapping.isNew && (
            <TextField
              label="Nuevo campo"
              value={mapping.label || ""}
              onChange={event => updateMapping(index, { label: event.target.value })}
              variant="outlined"
              margin="dense"
              size="small"
              fullWidth
            />
          )}
        </>
      ) : (
        <Typography className={classes.mappingStaticLabel} variant="body2">
          {mapping.label}
        </Typography>
      );

    return (
      <div className={classes.inboundMappingRow} key={`${mapping.target}-${index}`}>
        {label}
        <FormControl variant="outlined" margin="dense" fullWidth disabled={disabled}>
          <InputLabel>Variables recibidas</InputLabel>
          <Select
            multiple
            value={mapping.paths || []}
            onChange={event => updateMapping(index, { paths: event.target.value })}
            label="Variables recibidas"
            renderValue={selected => selected.join(", ")}
          >
            {sampleFields.map(field => (
              <MenuItem key={field} value={field}>
                <Checkbox
                  color="primary"
                  checked={(mapping.paths || []).indexOf(field) > -1}
                />
                {field}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <div className={classes.mappingActionColumn}>
          {mapping.target === "extra" && (
            <Button
              size="small"
              color="secondary"
              onClick={() => removeCustomMapping(index)}
            >
              Quitar campo
            </Button>
          )}
        </div>
      </div>
    );
  };

  const renderAutomationSamplePreview = () => {
    const sampleFields = editingAutomation?.sampleFields || [];
    const samplePayload = editingAutomation?.samplePayload || {};

    if (!sampleFields.length) {
      return (
        <div className={`${classes.statusBox} ${classes.warningBox}`}>
          <Typography variant="subtitle2">Esperando datos del webhook</Typography>
          <Typography variant="body2">
            Envia una peticion de prueba para detectar las variables disponibles.
          </Typography>
          <div className={classes.samplePreviewActions}>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<Refresh />}
              onClick={retryAutomationListen}
            >
              Reintentar escucha
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className={`${classes.statusBox} ${classes.successBox}`}>
        <Typography variant="subtitle2">Datos recibidos correctamente</Typography>
        <Typography variant="body2">
          Vista previa de las variables detectadas en la ultima peticion.
        </Typography>
        <div className={classes.samplePreview}>
          <div className={classes.samplePreviewHeader}>
            <span>Campo</span>
            <span>Valor recibido</span>
          </div>
          {sampleFields.map(field => (
            <div className={classes.samplePreviewRow} key={field}>
              <span className={classes.samplePreviewKey}>{field}</span>
              <span className={classes.samplePreviewValue}>
                {formatSampleValue(valueFromPath(samplePayload, field))}
              </span>
            </div>
          ))}
        </div>
        <div className={classes.samplePreviewActions}>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<Refresh />}
            onClick={retryAutomationListen}
          >
            Escuchar otra prueba
          </Button>
        </div>
      </div>
    );
  };

  const renderOutboundMappingRow = (mapping, index) => (
    <div className={classes.mappingRow} key={`outbound-mapping-${index}`}>
      <TextField
        label="Nombre en payload"
        value={mapping.key || ""}
        onChange={event => updateOutboundMapping(index, { key: event.target.value })}
        variant="outlined"
        margin="dense"
        size="small"
        placeholder="contacto.telefono"
      />
      <div>
        <FormControl variant="outlined" margin="dense" fullWidth>
          <InputLabel>Dato a enviar</InputLabel>
          <Select
            value={mapping.source || NO_MAPPING_SOURCE}
            onChange={event =>
              updateOutboundMapping(index, {
                source: event.target.value === NO_MAPPING_SOURCE ? "" : event.target.value,
              })
            }
            label="Dato a enviar"
          >
            <MenuItem value={NO_MAPPING_SOURCE}>Sin dato</MenuItem>
            {outboundSourceOptionsWithCustomFields.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          size="small"
          color="secondary"
          onClick={() => removeOutboundMapping(index)}
          disabled={outboundForm.payloadMappings.length === 1}
        >
          Quitar campo
        </Button>
      </div>
    </div>
  );

  const openOutboundLogs = async outbound => {
    setLogsOutbound(outbound);
    setOutboundLogs([]);
    setLoadingOutboundLogs(true);

    try {
      const { data } = await api.get(`/webhooks/outbounds/${outbound.id}/logs`);
      setOutboundLogs(data.logs || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingOutboundLogs(false);
    }
  };

  const openAutomationLogs = async automation => {
    setLogsAutomation(automation);
    setAutomationLogs([]);
    setLoadingAutomationLogs(true);

    try {
      const { data } = await api.get(`/webhooks/automations/${automation.id}/logs`);
      setAutomationLogs(data.logs || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingAutomationLogs(false);
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingWebhook ? `Eliminar webhook de entrada texto directo ${deletingWebhook.name}?` : "Eliminar webhook de entrada texto directo"}
        open={Boolean(deletingWebhook)}
        onClose={() => setDeletingWebhook(null)}
        onConfirm={deleteWebhook}
      >
        Esta accion eliminara el webhook de entrada texto directo y sus logs.
      </ConfirmationModal>

      <ConfirmationModal
        title={
          deletingOutbound
            ? `Eliminar webhook de salida ${deletingOutbound.name}?`
            : "Eliminar webhook de salida"
        }
        open={Boolean(deletingOutbound)}
        onClose={() => setDeletingOutbound(null)}
        onConfirm={deleteOutbound}
      >
        Esta accion eliminara el webhook de salida y sus logs.
      </ConfirmationModal>

      <ConfirmationModal
        title={
          deletingAutomation
            ? `Eliminar webhook de entrada ${deletingAutomation.name}?`
            : "Eliminar webhook de entrada"
        }
        open={Boolean(deletingAutomation)}
        onClose={() => setDeletingAutomation(null)}
        onConfirm={deleteAutomation}
      >
        Esta accion eliminara el webhook de entrada y su URL dejara de recibir datos.
      </ConfirmationModal>

      <ConfirmationModal
        title={
          deletingAiWebhookTool
            ? `Eliminar herramienta IA ${deletingAiWebhookTool.name}?`
            : "Eliminar herramienta IA"
        }
        open={Boolean(deletingAiWebhookTool)}
        onClose={() => setDeletingAiWebhookTool(null)}
        onConfirm={deleteAiWebhookTool}
      >
        Esta accion eliminara la herramienta webhook de IA y sus logs.
      </ConfirmationModal>

      <ConfirmationModal
        title={
          resettingAiWebhookTool
            ? `Restablecer herramienta IA ${resettingAiWebhookTool.name}?`
            : "Restablecer herramienta IA"
        }
        open={Boolean(resettingAiWebhookTool)}
        onClose={() => setResettingAiWebhookTool(null)}
        onConfirm={resetAiWebhookTool}
      >
        Esta accion restaurara la plantilla, parametros y payload por defecto. La URL y la conexion configuradas se mantienen.
      </ConfirmationModal>

      <ConfirmationModal
        title={
          regeneratingWebhook
            ? `Regenerar token de ${regeneratingWebhook.name}?`
            : "Regenerar token"
        }
        open={Boolean(regeneratingWebhook)}
        onClose={() => setRegeneratingWebhook(null)}
        onConfirm={regenerateToken}
      >
        El token anterior dejara de funcionar inmediatamente.
      </ConfirmationModal>

      <MainHeader>
        <Title>Webhooks</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<Add />}
            onClick={openCreateAutomation}
          >
            Agregar entrada
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={openCreateOutbound}
          >
            Agregar salida
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<Add />}
            onClick={openCreateAiWebhookTool}
          >
            Agregar herramienta IA
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={openCreateModal}
          >
            Agregar texto directo
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.sectionHeader}>
          <div>
            <Typography variant="h6">Webhooks de entrada</Typography>
            <Typography variant="body2" color="textSecondary">
              Recibe datos externos, detecta campos y crea o actualiza contactos desde el mapeo configurado.
            </Typography>
          </div>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={openCreateAutomation}
          >
            Agregar entrada
          </Button>
        </div>

        <div className={classes.tableWrapper}>
          <Table size="small" className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Dispositivo</TableCell>
                <TableCell>URL</TableCell>
                <TableCell align="center">Estado</TableCell>
                <TableCell align="center">Procesadas</TableCell>
                <TableCell align="center">Fallidos</TableCell>
                <TableCell>Ultimo dato</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {automations.map(renderAutomationRow)}
              {!automations.length && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Sin webhooks de entrada configurados.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <div className={classes.mobileCards}>
          {automations.map(renderMobileAutomation)}
          {!automations.length && (
            <Typography variant="body2" color="textSecondary">
              Sin webhooks de entrada configurados.
            </Typography>
          )}
        </div>
      </Paper>

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.sectionHeader}>
          <div>
            <Typography variant="h6">Webhooks de salida</Typography>
            <Typography variant="body2" color="textSecondary">
              Envia un POST a una URL externa cuando ocurra una secuencia, etiqueta o cambio de etapa.
            </Typography>
          </div>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={openCreateOutbound}
          >
            Agregar salida
          </Button>
        </div>

        <div className={classes.tableWrapper}>
          <Table size="small" className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Tipo de disparador</TableCell>
                <TableCell>Aplicar a</TableCell>
                <TableCell>URL destino</TableCell>
                <TableCell align="center">Estado</TableCell>
                <TableCell align="center">Enviados</TableCell>
                <TableCell align="center">Fallidos</TableCell>
                <TableCell>Ultimo envio</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {outboundWebhooks.map(renderOutboundRow)}
              {!outboundWebhooks.length && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    Sin webhooks de salida configurados.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <div className={classes.mobileCards}>
          {outboundWebhooks.map(renderMobileOutbound)}
          {!outboundWebhooks.length && (
            <Typography variant="body2" color="textSecondary">
              Sin webhooks de salida configurados.
            </Typography>
          )}
        </div>
      </Paper>

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.sectionHeader}>
          <div>
            <Typography variant="h6">Herramientas IA</Typography>
            <Typography variant="body2" color="textSecondary">
              Permite que la IA consulte o envie datos a Make usando webhooks configurados como funciones.
            </Typography>
          </div>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={openCreateAiWebhookTool}
          >
            Agregar herramienta IA
          </Button>
        </div>

        <div className={classes.tableWrapper}>
          <Table size="small" className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Funcion</TableCell>
                <TableCell>Conexion</TableCell>
                <TableCell>URL Make</TableCell>
                <TableCell align="center">Estado</TableCell>
                <TableCell align="center">Correctas</TableCell>
                <TableCell align="center">Fallidas</TableCell>
                <TableCell>Ultimo uso</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {aiWebhookTools.map(renderAiWebhookRow)}
              {!aiWebhookTools.length && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    Sin herramientas IA configuradas.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <div className={classes.mobileCards}>
          {aiWebhookTools.map(renderMobileAiWebhook)}
          {!aiWebhookTools.length && (
            <Typography variant="body2" color="textSecondary">
              Sin herramientas IA configuradas.
            </Typography>
          )}
        </div>
      </Paper>

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.sectionHeader}>
          <div>
            <Typography variant="h6">Webhook de entrada texto directo</Typography>
            <Typography variant="body2" color="textSecondary">
              Recibe target, message y token desde AppScript u otra herramienta para enviar texto directo a tickets existentes.
            </Typography>
          </div>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={openCreateModal}
          >
            Agregar texto directo
          </Button>
        </div>

        <div className={classes.toolbar}>
          <Typography variant="body2" color="textSecondary">
            Endpoint general para integraciones que envian texto al CRM.
          </Typography>
          <Button variant="outlined" onClick={() => copyText(endpoint)}>
            Copiar endpoint
          </Button>
        </div>

        <div className={classes.tableWrapper}>
          <Table size="small" className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Conexion</TableCell>
                <TableCell>Token</TableCell>
                <TableCell align="center">Estado</TableCell>
                <TableCell align="center">Enviados</TableCell>
                <TableCell align="center">Fallidas</TableCell>
                <TableCell>Ultimo uso</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {webhooks.map(renderWebhookRow)}
              {!webhooks.length && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Sin webhooks de entrada texto directo configurados.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <div className={classes.mobileCards}>
          {webhooks.map(renderMobileWebhook)}
          {!webhooks.length && (
            <Typography variant="body2" color="textSecondary">
              Sin webhooks de entrada texto directo configurados.
            </Typography>
          )}
        </div>
      </Paper>

      <Dialog
        open={modalOpen}
        onClose={closeModal}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle className={classes.stickyDialogTitle}>
          <div className={classes.dialogTitleContent}>
            <span>{editingWebhook ? "Editar webhook de entrada texto directo" : "Agregar webhook de entrada texto directo"}</span>
            <FormControlLabel
              className={classes.activeSwitch}
              control={
                <Switch
                  color="primary"
                  checked={Boolean(form.isActive)}
                  onChange={event => updateForm({ isActive: event.target.checked })}
                />
              }
              label="Activo"
            />
          </div>
        </DialogTitle>
        <form onSubmit={saveWebhook}>
          <DialogContent dividers className={classes.dialogGrid}>
            <Typography variant="body2" color="textSecondary">
              Configura el token que una plataforma externa usara para enviar mensajes usando la conexion seleccionada.
            </Typography>
            <TextField
              label="Nombre"
              value={form.name}
              onChange={event => updateForm({ name: event.target.value })}
              variant="outlined"
              margin="dense"
              fullWidth
              autoFocus
            />
            <FormControl variant="outlined" margin="dense" fullWidth>
              <InputLabel>Conexion</InputLabel>
              <Select
                value={form.connectionId || NO_WEBHOOK_CONNECTION}
                onChange={event =>
                  updateForm({
                    connectionId:
                      event.target.value === NO_WEBHOOK_CONNECTION ? "" : event.target.value,
                  })
                }
                label="Conexion"
              >
                <MenuItem value={NO_WEBHOOK_CONNECTION}>Sin conexion</MenuItem>
                {connections.map(connection => (
                  <MenuItem key={connection.id} value={connection.id}>
                    {connection.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {editingWebhook && (
              <>
                <Typography variant="subtitle2">Funcion AppScript</Typography>
                <div className={classes.codeBox}>{buildAppScript(editingWebhook)}</div>
                <Button
                  variant="outlined"
                  onClick={() => copyText(buildAppScript(editingWebhook))}
                >
                  Copiar funcion
                </Button>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeModal} color="secondary" variant="outlined">
              Cancelar
            </Button>
            <Button type="submit" color="primary" variant="contained">
              Guardar texto directo
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={outboundModalOpen}
        onClose={closeOutboundModal}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle className={classes.stickyDialogTitle}>
          <div className={classes.dialogTitleContent}>
            <span>{editingOutbound ? "Editar webhook de salida" : "Agregar webhook de salida"}</span>
            <FormControlLabel
              className={classes.activeSwitch}
              control={
                <Switch
                  color="primary"
                  checked={Boolean(outboundForm.isActive)}
                  onChange={event => updateOutboundForm({ isActive: event.target.checked })}
                />
              }
              label="Activo"
            />
          </div>
        </DialogTitle>
        <form onSubmit={saveOutbound}>
          <DialogContent dividers className={classes.dialogGrid}>
            <Typography variant="body2" color="textSecondary">
              Define el evento interno que dispara este webhook, la URL destino y los datos que se enviaran en el POST.
            </Typography>

            <TextField
              label="Nombre"
              value={outboundForm.name}
              onChange={event => updateOutboundForm({ name: event.target.value })}
              variant="outlined"
              margin="dense"
              fullWidth
              autoFocus
            />

            <FormControl variant="outlined" margin="dense" fullWidth>
              <InputLabel>Tipo de disparador</InputLabel>
              <Select
                value={outboundForm.triggerType}
                onChange={event =>
                  updateOutboundForm({
                    triggerType: event.target.value,
                    triggerReferenceId: "",
                  })
                }
                label="Tipo de disparador"
              >
                {outboundTriggerOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {renderOutboundReferenceSelector()}

            <TextField
              label="URL destino"
              value={outboundForm.url}
              onChange={event => updateOutboundForm({ url: event.target.value })}
              variant="outlined"
              margin="dense"
              fullWidth
              placeholder="https://..."
            />

            <div className={classes.inlineSectionHeader}>
              <div>
                <Typography variant="subtitle2">Payload a enviar</Typography>
                <Typography variant="body2" color="textSecondary">
                  Escoge que datos internos salen y con que nombre viajan en el JSON.
                </Typography>
              </div>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<Add />}
                onClick={addOutboundMapping}
              >
                Anadir campo
              </Button>
            </div>

            {outboundForm.payloadMappings.map(renderOutboundMappingRow)}
          </DialogContent>
          <DialogActions>
            <Button onClick={testOutbound} color="primary" variant="outlined">
              Ejecutar Webhook
            </Button>
            <Button onClick={closeOutboundModal} color="secondary" variant="outlined">
              Cancelar
            </Button>
            <Button type="submit" color="primary" variant="contained">
              Guardar salida
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={aiWebhookModalOpen}
        onClose={closeAiWebhookModal}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle className={classes.stickyDialogTitle}>
          <div className={classes.dialogTitleContent}>
            <span>{editingAiWebhookTool ? "Editar herramienta IA" : "Agregar herramienta IA"}</span>
            <FormControlLabel
              className={classes.activeSwitch}
              control={
                <Switch
                  color="primary"
                  checked={Boolean(aiWebhookForm.isActive)}
                  onChange={event =>
                    updateAiWebhookForm({ isActive: event.target.checked })
                  }
                />
              }
              label="Activo"
            />
          </div>
        </DialogTitle>
        <form onSubmit={saveAiWebhookTool}>
          <DialogContent dividers className={classes.dialogGrid}>
            <Typography variant="body2" color="textSecondary">
              Configura un webhook de Make como funcion que la IA puede usar durante la conversacion.
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Nombre"
                  value={aiWebhookForm.name}
                  onChange={event => {
                    const name = event.target.value;
                    updateAiWebhookForm({
                      name,
                      ...(!editingAiWebhookTool
                        ? { toolName: normalizeToolName(name) }
                        : {}),
                    });
                  }}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  autoFocus
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Nombre de funcion"
                  value={aiWebhookForm.toolName}
                  onChange={event =>
                    updateAiWebhookForm({
                      toolName: normalizeToolName(event.target.value),
                    })
                  }
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  placeholder="consultar_horarios_disponibles"
                />
              </Grid>
            </Grid>

            <TextField
              label="Descripcion para la IA"
              value={aiWebhookForm.description}
              onChange={event => updateAiWebhookForm({ description: event.target.value })}
              variant="outlined"
              margin="dense"
              fullWidth
              multiline
              minRows={2}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <FormControl variant="outlined" margin="dense" fullWidth>
                  <InputLabel>Metodo</InputLabel>
                  <Select
                    value={aiWebhookForm.method}
                    onChange={event => updateAiWebhookForm({ method: event.target.value })}
                    label="Metodo"
                  >
                    {["GET", "POST", "PUT", "PATCH", "DELETE"].map(method => (
                      <MenuItem key={method} value={method}>
                        {method}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={9}>
                <TextField
                  label="URL de Make"
                  value={aiWebhookForm.url}
                  onChange={event => updateAiWebhookForm({ url: event.target.value })}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  placeholder="https://hook.us1.make.com/..."
                />
              </Grid>
            </Grid>

            <FormControl variant="outlined" margin="dense" fullWidth>
              <InputLabel>Conexion</InputLabel>
              <Select
                value={aiWebhookForm.connectionId || ALL_AI_WEBHOOK_CONNECTIONS}
                onChange={event =>
                  updateAiWebhookForm({
                    connectionId:
                      event.target.value === ALL_AI_WEBHOOK_CONNECTIONS
                        ? ""
                        : event.target.value,
                  })
                }
                label="Conexion"
              >
                <MenuItem value={ALL_AI_WEBHOOK_CONNECTIONS}>Todas las conexiones</MenuItem>
                {connections.map(connection => (
                  <MenuItem key={connection.id} value={connection.id}>
                    {connection.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {shouldShowAiWebhookAvailability(aiWebhookForm) &&
              renderAiWebhookAvailabilitySettings()}

            <div className={classes.inlineSectionHeader}>
              <div>
                <Typography variant="subtitle2">Datos que la IA debe pedir</Typography>
                <Typography variant="body2" color="textSecondary">
                  Define los datos que la IA puede extraer del mensaje del cliente antes de llamar el webhook.
                </Typography>
              </div>
              <Button
                color="primary"
                variant="outlined"
                size="small"
                startIcon={<Add />}
                onClick={addAiWebhookParameter}
              >
                Agregar dato
              </Button>
            </div>

            {aiWebhookForm.parameters.map(renderAiWebhookParameterRow)}

            <div className={classes.inlineSectionHeader}>
              <div>
                <Typography variant="subtitle2">Datos que se envian a Make</Typography>
                <Typography variant="body2" color="textSecondary">
                  Escoge que datos viajan en el webhook y con que nombre los recibira Make.
                </Typography>
              </div>
              <Button
                color="primary"
                variant="outlined"
                size="small"
                startIcon={<Add />}
                onClick={addAiWebhookPayloadMapping}
              >
                Agregar campo
              </Button>
            </div>

            {aiWebhookForm.payloadMappings.map(renderAiWebhookPayloadRow)}

            <div className={classes.inlineSectionHeader}>
              <div>
                <Typography variant="subtitle2">Headers opcionales</Typography>
                <Typography variant="body2" color="textSecondary">
                  Agrega tokens o claves si el webhook de Make los requiere.
                </Typography>
              </div>
              <Button
                color="primary"
                variant="outlined"
                size="small"
                startIcon={<Add />}
                onClick={addAiWebhookHeader}
              >
                Agregar header
              </Button>
            </div>

            {aiWebhookForm.headers.length ? (
              aiWebhookForm.headers.map(renderAiWebhookHeaderRow)
            ) : (
              <Typography variant="body2" color="textSecondary">
                Sin headers configurados.
              </Typography>
            )}

            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={Boolean(aiWebhookForm.requiresConfirmation)}
                  onChange={event =>
                    updateAiWebhookForm({
                      requiresConfirmation: event.target.checked,
                    })
                  }
                />
              }
              label="Requiere confirmacion"
            />
          </DialogContent>
          <DialogActions>
            {editingAiWebhookTool && (
              <Button onClick={testAiWebhookTool} color="primary" variant="outlined">
                Ejecutar Webhook
              </Button>
            )}
            <Button onClick={closeAiWebhookModal} color="secondary" variant="outlined">
              Cancelar
            </Button>
            <Button type="submit" color="primary" variant="contained">
              Guardar herramienta
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={automationModalOpen}
        onClose={closeAutomationModal}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle className={classes.stickyDialogTitle}>
          <div className={classes.dialogTitleContent}>
            <span>Configurar webhook de entrada</span>
            <FormControlLabel
              className={classes.activeSwitch}
              control={
                <Switch
                  color="primary"
                  checked={Boolean(automationForm.isActive)}
                  onChange={event =>
                    updateAutomationForm({ isActive: event.target.checked })
                  }
                />
              }
              label="Activo"
            />
          </div>
        </DialogTitle>
        <form onSubmit={saveAutomation}>
          <DialogContent dividers className={classes.dialogGrid}>
            <Typography variant="body2" color="textSecondary">
              Define que datos recibe este webhook, como se mapean al contacto y que secuencias debe ejecutar.
            </Typography>

            <TextField
              label="Nombre"
              value={automationForm.name}
              onChange={event => updateAutomationForm({ name: event.target.value })}
              variant="outlined"
              margin="dense"
              fullWidth
              autoFocus
            />

            <FormControl variant="outlined" margin="dense" fullWidth>
              <InputLabel>Tipo de disparador</InputLabel>
              <Select
                value={automationForm.triggerType}
                onChange={event => updateAutomationForm({ triggerType: event.target.value })}
                label="Tipo de disparador"
              >
                <MenuItem value="third_party">Integracion con terceros</MenuItem>
              </Select>
            </FormControl>

            <FormControl variant="outlined" margin="dense" fullWidth>
              <InputLabel>Seleccionar dispositivo</InputLabel>
              <Select
                value={automationForm.connectionId || NO_AUTOMATION_CONNECTION}
                onChange={event =>
                  updateAutomationForm({
                    connectionId:
                      event.target.value === NO_AUTOMATION_CONNECTION
                        ? ""
                        : event.target.value,
                  })
                }
                label="Seleccionar dispositivo"
              >
                <MenuItem value={NO_AUTOMATION_CONNECTION}>Sin dispositivo</MenuItem>
                {connections.map(connection => (
                  <MenuItem key={connection.id} value={connection.id}>
                    {connection.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <div>
              <Typography variant="subtitle2" gutterBottom>
                Copia esta URL y configurala en tu plataforma externa
              </Typography>
              <div className={classes.endpointBox}>
                <span className={classes.endpointText}>{automationEndpoint}</span>
                <Tooltip title="Copiar URL">
                  <IconButton size="small" onClick={() => copyText(automationEndpoint)}>
                    <FileCopyOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
            </div>

            {renderAutomationSamplePreview()}

            <div>
              <Typography variant="subtitle2">
                Relaciona los datos del webhook con la informacion de tus contactos
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Puedes asignar una o varias variables al mismo campo.
              </Typography>
            </div>

            {automationForm.fieldMappings.map(renderMappingRow)}

            <Button color="primary" onClick={addCustomMapping}>
              Agregar campo personalizado
            </Button>

            <FormControl variant="outlined" margin="dense" fullWidth>
              <InputLabel>Frecuencia de activacion por contacto</InputLabel>
              <Select
                value={automationForm.frequency}
                onChange={event => updateAutomationForm({ frequency: event.target.value })}
                label="Frecuencia de activacion por contacto"
              >
                <MenuItem value="always">Cada vez que se cumpla la condicion</MenuItem>
                <MenuItem value="once">Solo una vez por contacto</MenuItem>
              </Select>
            </FormControl>

            <div>
              <Typography variant="subtitle2">Secuencia inmediata</Typography>
              {renderAutomationSequenceSelector()}
            </div>

            <div className={classes.inlineSectionHeader}>
              <Typography variant="subtitle2">Countdowns programados</Typography>
              <Button
                color="primary"
                variant="outlined"
                size="small"
                startIcon={<Add />}
                onClick={addCountdown}
              >
                Agregar countdown
              </Button>
            </div>

            <div className={classes.dialogGrid}>
              {normalizeCountdowns(automationForm.countdowns).map((countdown, index) => (
                <Grid container spacing={1} key={`webhook-countdown-${index}`}>
                  <Grid item xs={4} md={2}>
                    <TextField
                      label="Antes"
                      type="number"
                      value={countdown.value}
                      onChange={event =>
                        updateCountdown(index, {
                          value: Math.max(0, Number(event.target.value)),
                        })
                      }
                      variant="outlined"
                      margin="dense"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={8} md={3}>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel>Unidad</InputLabel>
                      <Select
                        value={countdown.unit}
                        onChange={event =>
                          updateCountdown(index, { unit: event.target.value })
                        }
                        label="Unidad"
                      >
                        <MenuItem value="minutes">Minutos</MenuItem>
                        <MenuItem value="hours">Horas</MenuItem>
                        <MenuItem value="days">Dias</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={10} md={6}>
                    {renderAutomationSequenceSelector({
                      value: countdown.sequenceId,
                      onChange: sequenceId => updateCountdown(index, { sequenceId }),
                      target: { type: "countdown", index },
                    })}
                  </Grid>
                  <Grid item xs={2} md={1}>
                    <IconButton
                      size="small"
                      onClick={() => removeCountdown(index)}
                      disabled={normalizeCountdowns(automationForm.countdowns).length === 1}
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeAutomationModal} color="secondary" variant="outlined">
              Cancelar
            </Button>
            <Button type="submit" color="primary" variant="contained">
              Guardar entrada
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={Boolean(logsAutomation)} onClose={() => setLogsAutomation(null)} maxWidth="md" fullWidth>
        <DialogTitle>Logs de entrada: {logsAutomation?.name}</DialogTitle>
        <DialogContent dividers>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Contacto</TableCell>
                <TableCell>Ticket</TableCell>
                <TableCell>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {automationLogs.map(log => (
                <TableRow key={log.id}>
                  <TableCell>{formatDate(log.createdAt)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={
                        log.status === "success"
                          ? "Procesado"
                          : log.status === "skipped"
                          ? "Omitido"
                          : "Fallido"
                      }
                      className={
                        log.status === "success"
                          ? classes.logStatusSuccess
                          : log.status === "failed"
                          ? classes.logStatusFailed
                          : undefined
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {log.contact
                      ? `${log.contact.name || ""} ${log.contact.number || ""}`.trim()
                      : log.ticket?.contact
                      ? `${log.ticket.contact.name || ""} ${log.ticket.contact.number || ""}`.trim()
                      : "-"}
                  </TableCell>
                  <TableCell>
                    {log.ticket
                      ? `#${log.ticket.id} ${log.ticket.contact?.name || ""}`.trim()
                      : "-"}
                  </TableCell>
                  <TableCell>{log.error || "-"}</TableCell>
                </TableRow>
              ))}
              {!loadingAutomationLogs && !automationLogs.length && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Sin logs.
                  </TableCell>
                </TableRow>
              )}
              {loadingAutomationLogs && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Cargando...
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogsAutomation(null)} color="primary" variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(logsWebhook)} onClose={() => setLogsWebhook(null)} maxWidth="md" fullWidth>
        <DialogTitle>Logs de entrada texto directo: {logsWebhook?.name}</DialogTitle>
        <DialogContent dividers>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>Destino</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Ticket</TableCell>
                <TableCell>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map(log => (
                <TableRow key={log.id}>
                  <TableCell>{formatDate(log.createdAt)}</TableCell>
                  <TableCell>{log.target}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={log.status === "success" ? "Enviado" : "Fallido"}
                      className={
                        log.status === "success"
                          ? classes.logStatusSuccess
                          : classes.logStatusFailed
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {log.ticket
                      ? `#${log.ticket.id} ${log.ticket.contact?.name || ""}`
                      : "-"}
                  </TableCell>
                  <TableCell>{log.error || "-"}</TableCell>
                </TableRow>
              ))}
              {!loadingLogs && !logs.length && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Sin logs.
                  </TableCell>
                </TableRow>
              )}
              {loadingLogs && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Cargando...
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogsWebhook(null)} color="primary" variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(logsOutbound)} onClose={() => setLogsOutbound(null)} maxWidth="md" fullWidth>
        <DialogTitle>Logs de salida: {logsOutbound?.name}</DialogTitle>
        <DialogContent dividers>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>Disparador</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Codigo</TableCell>
                <TableCell>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {outboundLogs.map(log => (
                <TableRow key={log.id}>
                  <TableCell>{formatDate(log.createdAt)}</TableCell>
                  <TableCell>{formatOutboundTrigger(log.triggerType)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={log.status === "success" ? "Enviado" : "Fallido"}
                      className={
                        log.status === "success"
                          ? classes.logStatusSuccess
                          : classes.logStatusFailed
                      }
                    />
                  </TableCell>
                  <TableCell>{log.responseStatus || "-"}</TableCell>
                  <TableCell>{log.error || "-"}</TableCell>
                </TableRow>
              ))}
              {!loadingOutboundLogs && !outboundLogs.length && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Sin logs.
                  </TableCell>
                </TableRow>
              )}
              {loadingOutboundLogs && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Cargando...
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogsOutbound(null)} color="primary" variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(logsAiWebhookTool)} onClose={() => setLogsAiWebhookTool(null)} maxWidth="md" fullWidth>
        <DialogTitle>Logs de herramienta IA: {logsAiWebhookTool?.name}</DialogTitle>
        <DialogContent dividers>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Codigo</TableCell>
                <TableCell>Ticket</TableCell>
                <TableCell>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {aiWebhookLogs.map(log => (
                <TableRow key={log.id}>
                  <TableCell>{formatDate(log.createdAt)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={log.status === "success" ? "Correcto" : "Fallido"}
                      className={
                        log.status === "success"
                          ? classes.logStatusSuccess
                          : classes.logStatusFailed
                      }
                    />
                  </TableCell>
                  <TableCell>{log.responseStatus || "-"}</TableCell>
                  <TableCell>
                    {log.ticket
                      ? `#${log.ticket.id} ${log.ticket.contact?.name || ""}`
                      : "-"}
                  </TableCell>
                  <TableCell>{log.error || "-"}</TableCell>
                </TableRow>
              ))}
              {!loadingAiWebhookLogs && !aiWebhookLogs.length && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Sin logs.
                  </TableCell>
                </TableRow>
              )}
              {loadingAiWebhookLogs && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Cargando...
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogsAiWebhookTool(null)} color="primary" variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <ChatbotModal
        open={sequenceModalOpen}
        onClose={handleSequenceModalClose}
        onSave={handleSequenceSaved}
        sequenceId={sequenceModalId}
        defaultConnectionId={automationForm.connectionId}
        initialSequenceName="Secuencia de webhook"
        initialSequenceCategory="Webhooks"
        categories={["Webhooks"]}
        extraVariables={sequenceExtraVariables}
      />
    </MainContainer>
  );
};

export default Webhooks;
